/**
 * Sentry grouping key for a route error surfaced by the root ErrorBoundary.
 *
 * Every status is thrown from one line in `root.tsx`, so without an explicit
 * fingerprint Sentry collapses 401, 404, 431, 503 and 504 onto a single issue
 * and a real outage is invisible next to routine auth noise.
 *
 * The route part is a React Router route id rather than a pathname, so
 * `routes/_storefront._products.product.$slug.$productId` stays one bucket
 * where a pathname would mint a new issue per product and per query string.
 *
 * Shares the `route-error` shape with `throwRouteError` in `error.ts` but not
 * its vocabulary: that one passes a hand-written page name, so client and
 * server reports of the same failure stay in separate issues.
 *
 * Lives in its own file (instead of inline in `root.tsx`) so the unit test can
 * import it without dragging in the session and secrets bootstrap chain — the
 * same reason `assert-not-aborted.ts` is separate.
 */
export function buildRouteErrorFingerprint(
	status: number,
	routeId: string,
): string[] {
	return ["route-error", String(status), routeId];
}

/**
 * The deepest matched route, which is not always the one that threw: a parent
 * loader's error reports the child it was rendering under. Returned as a string
 * so callers can use it directly as an effect dependency — `useMatches()`
 * returns a fresh array each render, which would re-run the effect and capture
 * the same error twice.
 */
export function getDeepestRouteId(matches: readonly { id: string }[]): string {
	return matches.at(-1)?.id ?? "unknown";
}
